<template>
  <div v-if="addressStore.isLoading && isEditing">
    <span class="loading loading-spinner loading-lg text-base-content/60"></span>
  </div>
  <div v-else class="mt-6 bg-base-100 rounded-lg p-6">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-semibold">
        {{ isEditing ? "Redaguoti adresą" : "Naujas adresas" }}
      </h2>

      <button
        v-if="isEditing"
        @click="confirmDelete(formData.id)"
        class="btn btn-sm btn-ghost"
        title="Ištrinti"
      >
        <DefaultIcon name="trash" size="w-5 h-5" />
      </button>
    </div>

    <DefaultAlert
      text="Vietą galite pasirinkti susiradę adresą paieškoje arba spustelėję žemėlapyje. Norėdami pakeisti vietą, spustelėkite žemėlapyje kitą tašką arba tempkite žymeklį."
      icon="info"
      :dismissible="true"
    />

    <form @submit.prevent="handleSubmit" class="space-y-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Pavadinimas*</legend>
          <input
            v-model="formData.title"
            type="text"
            class="input w-full"
            :class="{ 'input-error': errors.title }"
          />
          <p v-if="errors.title" class="fieldset-label text-error">
            {{ errors.title[0] }}
          </p>
        </fieldset>

        <AddressSearchInput
          v-model="formData.address"
          :required="false"
          label="Adresas (jei egzistuoja)"
          class="w-full"
          @coordinates-change="handleCoordinatesChange"
        />
      </div>

      <fieldset class="fieldset">
        <legend class="fieldset-legend">Vietos taškas žemėlapyje</legend>
        <AddressMapPicker
          :lat="formData.lat"
          :lng="formData.lng"
          height="350px"
          @update:coordinates="handleCoordinatesChange"
        />
        <p v-if="errors.lat || errors.lng" class="fieldset-label text-error">
          {{ (errors.lat || errors.lng)[0] }}
        </p>
      </fieldset>

      <div class="flex justify-end gap-4 mt-8">
        <button type="button" class="btn btn-ghost" @click="goBack('/addresses')">
          Atšaukti
        </button>
        <button
          type="submit"
          class="btn btn-success"
          :disabled="addressStore.isLoading"
        >
          {{ isEditing ? "Atnaujinti" : "Pridėti" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { useAddressStore } from "@/stores/addresses";
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useGoBack } from "@/composables/navigation/useGoBack";
import AddressSearchInput from "@/components/search/AddressSearchInput.vue";
import AddressMapPicker from "@/components/maps/AddressMapPicker.vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import DefaultAlert from "@/components/ui/alerts/DefaultAlert.vue";

const route = useRoute();
const goBack = useGoBack();
const addressStore = useAddressStore();

const isEditing = computed(() => !!route.params.id);
const errors = ref({});

const formData = ref({
  title: null,
  address: null,
  lat: null,
  lng: null,
});

onMounted(async () => {
  if (isEditing.value) {
    try {
      const address = await addressStore.fetchAddressById(route.params.id);
      if (address) {
        formData.value = {
          ...address,
          lat: parseFloat(address.lat),
          lng: parseFloat(address.lng),
        };
      } else {
        goBack("/addresses");
      }
    } catch (error) {
      console.error("Failed to fetch address:", error);
      goBack("/addresses");
    }
  }
});

const handleCoordinatesChange = ({ lat, lng }) => {
  formData.value.lat = lat;
  formData.value.lng = lng;
};

const handleSubmit = async () => {
  errors.value = {};

  try {
    if (isEditing.value) {
      await addressStore.updateAddress(route.params.id, formData.value);
    } else {
      await addressStore.addAddress(formData.value);
    }
    goBack("/addresses");
  } catch (err) {
    errors.value = err.response?.data?.errors || {};
  }
};

const confirmDelete = async (id) => {
  if (confirm("Ar tikrai norite ištrinti šį adresą?")) {
    try {
      await addressStore.deleteAddress(id);
      goBack("/addresses");
    } catch (error) {
      console.error("Failed to delete address:", error);
    }
  }
};
</script>
